import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import {
	useAuthenticatedUser,
	useLocalizedCalendarDay,
	useViewerRoles,
} from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	Button,
	ComposeTweetIcon,
	ContextMenu,
	DeleteIcon,
	FlexGap,
	IconButton,
	Label,
	MoreIcon,
	Paragraph,
	PencilEditBoxIcon,
	QuillText,
	Tooltip,
	tokens,
} from '@treams/treams-ui';

import Clip from '../../../../packages/kakapo-ui-kit/components/common/Clip';
import HashLight from '../../../../packages/kakapo-ui-kit/components/common/HashLight';
import useDetectPrint from '../../hooks/useDetectPrint';
import EvaluationNoteComposerModal from './EvaluationNoteComposerModal';
import EvaluationNoteEditorModal from './EvaluationNoteEditorModal';
import EvaluationNoteModal from './EvaluationNoteModal';

const Card = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${tokens.gap.inline}px;
	width: 100%;
	box-sizing: border-box;
	padding: ${tokens.padding.control}px;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.subtle};
	border-radius: ${tokens.radius.sm}px;
`;

const AvatarLink = styled(Link)`
	display: flex;
	flex-shrink: 0;
`;

const NameLink = styled(Link)`
	color: inherit;
	text-decoration: none;
	min-width: 0;

	&:hover {
		text-decoration: underline;
	}
`;

const Name = styled(Label)`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const Body = styled.div`
	width: 100%;
	color: ${tokens.text.secondary};
	font-size: ${tokens.typography.sizes.small}px;
	font-weight: ${tokens.typography.weights.regular};
	line-height: ${tokens.typography.lineHeights.small};
	letter-spacing: 0.01em;
	font-family: ${tokens.typography.fontFamily};

	.ql-editor,
	.ql-editor p,
	.ql-editor li {
		font-size: inherit;
		color: inherit;
		line-height: inherit;
		font-weight: inherit;
	}
`;

function EvaluationNote(props: { comment: graphql.CommentFragment; submissionID: string }) {
	const { t } = useTranslation();
	const [modal, setModal] = useState(false);
	const isPrinting = useDetectPrint();

	const { isWorkspaceSuperuser } = useViewerRoles();
	const authenticatedUser = useAuthenticatedUser();
	const [archive] = graphql.useArchiveCommentMutation();

	const canRemove = isWorkspaceSuperuser || props.comment.user.id === authenticatedUser.id;
	const canEdit = props.comment.viewerCanUpdate;
	const showMenu = !isPrinting && (canEdit || canRemove);
	const name = `${props.comment.user.firstName} ${props.comment.user.lastName}`;
	const createdAtLabel = useLocalizedCalendarDay(props.comment.createdAt, { capitalize: true });

	const remove = async () => {
		const confirmed = window.confirm(t('comments.item.remove.buttonTitle'));

		if (!confirmed) {
			return;
		}

		await archive({
			variables: {
				input: {
					id: props.comment.id,
				},
			},
		});
	};

	return (
		<>
			<Box width="100%">
				<HashLight name={`comment-${props.comment.id}`}>
					<Card>
						<FlexGap
							gap={tokens.gap.element}
							alignItems="flex-start"
							justifyContent="space-between"
							width="100%"
						>
							<FlexGap gap={tokens.gap.element} alignItems="center" minWidth={0}>
								<AvatarLink to={`/users/${props.comment.user.id}`}>
									<Avatar user={props.comment.user} size={20} flexShrink={0} />
								</AvatarLink>
								<FlexGap flexDirection="column" gap={0} minWidth={0}>
									<NameLink to={`/users/${props.comment.user.id}`}>
										<Name>{name}</Name>
									</NameLink>
									<Tooltip
										content={moment(props.comment.createdAt).format('LLL')}
										position="top"
										portal
										fitContent
									>
										<Paragraph small m={0}>
											{createdAtLabel}
										</Paragraph>
									</Tooltip>
								</FlexGap>
							</FlexGap>
							{showMenu && (
								<ContextMenu.Target
									menu={
										<ContextMenu>
											{canEdit && (
												<ContextMenu.Item
													icon={<PencilEditBoxIcon />}
													title={t('common.edit')}
													onClick={() => setModal(true)}
												/>
											)}
											{canRemove && (
												<ContextMenu.Item
													icon={<DeleteIcon />}
													title={t('common.delete')}
													type="destructive"
													onClick={remove}
												/>
											)}
										</ContextMenu>
									}
								>
									<IconButton type="plain" icon={<MoreIcon />} />
								</ContextMenu.Target>
							)}
						</FlexGap>
						<Clip onClick={() => setModal(true)}>
							<Body>
								<QuillText value={props.comment.content} />
							</Body>
						</Clip>
					</Card>
				</HashLight>
			</Box>
			{modal &&
				(canEdit ? (
					<EvaluationNoteEditorModal
						comment={props.comment}
						submissionID={props.submissionID}
						onDismiss={() => setModal(false)}
					/>
				) : (
					<EvaluationNoteModal comment={props.comment} onClose={() => setModal(false)} />
				))}
		</>
	);
}

export default function EvaluationNotes(props: {
	submission: graphql.EvaluationSubmissionFragment;
	itemID?: string;
}) {
	const { t } = useTranslation();
	const isPrinting = useDetectPrint();

	const [modal, setModal] = useState(false);

	const comments = props.submission.comments
		.filter(
			(comment) =>
				new Map(comment.metadata?.map((metadata) => [metadata.key, metadata.value])).get(
					'formItemID',
				) === props.itemID,
		)
		.filter((comment) => !comment.isArchived);

	if (isPrinting || (!props.submission.viewer.viewerCanUpdate && comments.length === 0)) {
		return null;
	}

	const sortedComments = comments.sort((a, b) => {
		return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
	});

	return (
		<FlexGap flexDirection="column" gap={tokens.gap.list} width="100%">
			{sortedComments.map((comment) => (
				<EvaluationNote key={comment.id} comment={comment} submissionID={props.submission.id} />
			))}
			{!isPrinting && props.submission.viewer.viewerCanUpdate && props.itemID && (
				<Box>
					<Button
						type="secondary"
						icon={<ComposeTweetIcon />}
						title={t('evaluation.submission.notes.addButton.title')}
						onClick={() => setModal(true)}
					/>
				</Box>
			)}
			{modal && (
				<EvaluationNoteComposerModal
					submissionID={props.submission.id}
					itemID={props.itemID}
					onDismiss={() => setModal(false)}
				/>
			)}
		</FlexGap>
	);
}
